import { Box, FormControl, FormHelperText, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { FC } from 'react';
import { BasicRawMaterialCategory } from 'src/models/Management.interface';
import {
  StyledUnitSelect,
  StyledUnitMenuItem,
  unitSelectMenuProps,
} from 'src/components/StyledComponents/StyledUnitPicker';
import ExpandMoreOutlined from '@mui/icons-material/ExpandMoreOutlined';

const StyledCategoryLabel = styled(Typography)`
  font-family: Outfit;
  font-weight: 400;
  font-size: 16px;
  color: var(--primary-navy);
  margin-bottom: 16px;
`;

const StyledFormControl = styled(FormControl)`
  margin: 0;
`;

const StyledErrorText = styled(FormHelperText)`
  font-family: Outfit;
  font-weight: 400;
  font-size: 14px;
  color: var(--error);
  display: flex;
  align-items: center;
  margin-left: 0;
  margin-top: 8px;
`;

const StyledErrorIcon = styled(Box)`
  font-size: 16px;
  color: var(--error);
  margin-right: 8px;
`;

interface CategoryPickerProps {
  id: string;
  label: string;
  categories: BasicRawMaterialCategory[];
  value: string;
  onChange: (value: string) => void;
  width?: string;
  height?: string;
  error?: string;
  allowNone?: boolean;
  noneLabel?: string;
}

const CategoryPicker: FC<CategoryPickerProps> = ({
  id,
  label,
  categories,
  value,
  onChange,
  width = '300px',
  height = '40px',
  error,
  allowNone = false,
  noneLabel = 'None',
}) => {
  const hasError = Boolean(error);

  return (
    <Box mb={3}>
      <StyledCategoryLabel>{label}</StyledCategoryLabel>
      <StyledFormControl fullWidth error={hasError}>
        <StyledUnitSelect
          id={id}
          name={id}
          value={value}
          onChange={(e) => onChange(e.target.value as string)}
          displayEmpty
          height={height}
          width={width}
          hasError={hasError}
          IconComponent={ExpandMoreOutlined}
          renderValue={(selected) =>
            selected
              ? categories.find((cat) => cat.id === selected)?.name ||
                `Select ${label}`
              : allowNone
              ? noneLabel
              : `Select ${label}`
          }
          MenuProps={unitSelectMenuProps}
        >
          {allowNone && (
            <StyledUnitMenuItem value="">
              <em>{noneLabel}</em>
            </StyledUnitMenuItem>
          )}
          {categories.map((category) => (
            <StyledUnitMenuItem key={category.id} value={category.id}>
              {category.name}
            </StyledUnitMenuItem>
          ))}
        </StyledUnitSelect>
        {error && (
          <StyledErrorText>
            <StyledErrorIcon component="i" className="bx bxs-error-circle" />
            {error}
          </StyledErrorText>
        )}
      </StyledFormControl>
    </Box>
  );
};

export default CategoryPicker;
